import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import { utils, FixedNumber, BigNumber } from 'ethers';
import { findTokenBySymbol } from '../../datas/tokens';
import { findChainBySymbol } from '../../datas/chains';
import { HistoryPayload } from '../../services/bridge-backend.service';
import { useAppSelector } from '../../hooks';
import { selectBridgeSlice } from '../../redux/slices/bridgeSlice';
import { convertToExactAddress } from '../../utils/common';

export type TransactionStatus = 'is-ing' | 'is-fail' | '';

type ExchangeListItem = {
  data: HistoryPayload;
  onRequestRefund: () => void;
  onRequestConfirm: () => void;
};

const TransactionTableItem: React.FunctionComponent<ExchangeListItem> = ({
  data,
  onRequestRefund,
  onRequestConfirm,
}) => {
  const { isConfirm } = useAppSelector(selectBridgeSlice);
  const token = findTokenBySymbol(data.tokenSymbol, data.fromChainSymbol);
  const amount = FixedNumber.from(
    utils.formatUnits(BigNumber.from(data.amount), token?.decimals),
  )
    .round(6)
    .toString();

  const fromChainLabel = useMemo(() => {
    // fromChain 상태가 Rejected일 경우
    if (
      data.fromTxHash ===
      `0x0000000000000000000000000000000000000000000000000000000000000000`
    ) {
      return `Rejected`;
    }
    // fromChain상태가 Pending일 경우
    if (
      data.fromChainHtlcState === null ||
      data.fromChainHtlcState === `pending`
    ) {
      if (
        data.toChainHtlcState === `pending` ||
        data.toChainHtlcState === null
      ) {
        return `Pending`;
      }
    }

    // fromChain 상태가 Awaiting refund일 경우
    if (data.fromChainHtlcState === `mirrored`) {
      if (
        data.toChainHtlcState === `expired` ||
        data.toChainHtlcState === `refunded`
      )
        return `Awaiting refund`;
    }

    // fromChain 상태가 Complete일 경우
    if (
      data.fromChainHtlcState === `mirrored` ||
      data.fromChainHtlcState === `withdrawn` ||
      data.fromChainHtlcState === `newContract` ||
      data.fromChainHtlcState === `expired`
    ) {
      if (
        data.toChainHtlcState === `pending` ||
        data.toChainHtlcState === `mirrored` ||
        data.toChainHtlcState === `withdrawn` ||
        data.toChainHtlcState === `refunded`
      ) {
        return `Complete`;
      }
    }

    // fromChain 상태가 Refunded일 경우
    if (
      data.fromChainHtlcState === `refunded` &&
      data.toChainHtlcState === `refunded`
    ) {
      return `Refunded`;
    }

    return `Failed`;
  }, [data.fromChainHtlcState, data.fromTxHash, data.toChainHtlcState]);

  const toChainLabel = useMemo(() => {
    if (
      data.fromTxHash ===
      `0x0000000000000000000000000000000000000000000000000000000000000000`
    ) {
      return `-`;
    }
    // toChain상태가 Pending일 경우
    if (
      data.toChainHtlcState === null ||
      data.toChainHtlcState === `pending` ||
      data.toChainHtlcState === `newContract` ||
      data.toChainHtlcState === `mirrored`
    ) {
      return `Pending`;
    }

    // toChain상태가 Complete일 경우
    if (
      data.toChainHtlcState === `withdrawn` &&
      data.fromChainHtlcState !== `expired`
    ) {
      return `Complete`;
    }

    // toChain상태가 Refunded일 경우

    if (data.fromChainHtlcState === `mirrored`) {
      if (
        data.toChainHtlcState === `expired` ||
        data.toChainHtlcState === `refunded`
      )
        return `Refunded`;
    }
    if (
      data.fromChainHtlcState === `expired` &&
      data.toChainHtlcState === `refunded`
    ) {
      return `Refunded`;
    }

    if (
      data.fromChainHtlcState === `refunded` &&
      data.toChainHtlcState === `refunded`
    ) {
      return `Refunded`;
    }

    if (
      data.fromChainHtlcState === `expired` &&
      data.toChainHtlcState === `expired`
    ) {
      return `Failed`;
    }

    return `Failed`;
  }, [data.fromChainHtlcState, data.fromTxHash, data.toChainHtlcState]);

  const trClassName = useMemo(() => {
    if (data.fromChainHtlcState === `failed`) {
      return `is-fail`;
    }
    if (
      data.fromChainHtlcState === null ||
      data.fromChainHtlcState === `pending` ||
      data.fromChainHtlcState === `newContract`
    ) {
      return `is-ing`;
    }
    return ``;
  }, [data.fromChainHtlcState]);

  const fromChain = findChainBySymbol(data.fromChainSymbol);
  const toChain = findChainBySymbol(data.toChainSymbol);

  return (
    <tr className={trClassName}>
      <td>
        <a
          href={
            fromChain
              ? fromChain.explorerUrl +
                fromChain.explorerAddressPrefix +
                data.sender
              : `#`
          }
          className="wallet-name"
          target="_blank"
          rel="noreferrer"
        >
          <i data-icon="window" />
          {convertToExactAddress(data.sender)}
        </a>
      </td>
      <td>{data.tokenSymbol}</td>
      <td>{amount}</td>
      <td>{dayjs(data.createdAt).utc().format(`YYYY-MM-DD HH:mm:ss`)}</td>
      <td>
        <span>{fromChain?.name}</span>
      </td>
      <td>
        <div>
          <div>{fromChainLabel}</div>
          {fromChainLabel === `Awaiting refund` && (
            <div className="date">
              (
              {dayjs(Number(data.fromChainTimelock) * 1000)
                .utc()
                .format(`YYYY-MM-DD HH:mm:ss`)}
              )
            </div>
          )}
          {data.fromChainHtlcState === `expired` && (
            <button className="t-btn" onClick={onRequestRefund}>
              Refund
            </button>
          )}
        </div>
      </td>
      <td>
        <a
          href={
            toChain
              ? toChain.explorerUrl +
                toChain.explorerAddressPrefix +
                data.receiver
              : `#`
          }
          className="wallet-name"
          target="_blank"
          rel="noreferrer"
        >
          <i data-icon="window" />
          {convertToExactAddress(data.receiver)}
        </a>
      </td>
      <td>
        <span>{toChain?.name}</span>
      </td>
      <td>
        <div>
          <div>{toChainLabel}</div>
          {data.toChainHtlcState === `mirrored` && (
            <button
              className="t-btn"
              onClick={onRequestConfirm}
              disabled={!!data.toTxHash || isConfirm}
            >
              Confirm
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default TransactionTableItem;
